import React, {FocusEventHandler, useState} from "react"
import {ClickOutsideHandler} from "../click-outside-handler/click-outside-handler"
import styles from "./select.module.scss"
import {ValidationResult} from "../../util/validation/validation-result"
import MaterialIcon from "../material-icon/material-icon-view"

export interface SelectItem {
  id: string
  selected: boolean
  separator?: boolean
  component?: React.ReactChild
}

export interface SelectProps {
  id?: string
  title?: React.ReactChild
  items: SelectItem[]
  disabled?: boolean
  showValidity?: boolean
  validationResult: ValidationResult
  collapseOnItemClick?: boolean
  iconClassName?: string
  personalData?: boolean
  onChange?: (selectedItem: SelectItem) => void
  readonly onBlur?: FocusEventHandler<HTMLInputElement>
  testId? : string
}

export const SelectTitle: React.FC = ({children}) => {
  return (
    <div className={styles.SelectTitle}>
      {children}
    </div>
  )
}

const Separator: React.FC = () => (
  <div className={styles.SeparatorItem}/>
)

export const Select: React.FC<SelectProps> = ({id, title, items, disabled = false,
  showValidity, validationResult, collapseOnItemClick = true, iconClassName,
  personalData = false, onChange, testId}) => {

  const [isOpen, setOpen] = useState(false)

  function handleClickedOutside() {
    return setOpen(false)
  }

  function handleComponentClick() {
    if (!disabled) setOpen(!isOpen)
  }

  function handleItemClick(item: SelectItem) {
    return (e: React.MouseEvent<HTMLDivElement>) => {
      if (onChange) onChange(item)
      if (!collapseOnItemClick) e.stopPropagation()
    }
  }

  const titleComponent = (typeof title === "string" || typeof title === "number")
    ? <div>{title}</div>
    : title

  const borderClass = () => {
    if (disabled) return ""
    if (!showValidity) return styles.DefaultBorderColor
    if (validationResult.valid) return styles.DefaultBorderColor
    if (!validationResult.valid) return styles.ErrorBorderColor
  }

  return (
    <ClickOutsideHandler onClickedOutside={handleClickedOutside}>
      <div id={id} className={styles.Container} onClick={handleComponentClick} data-testid={testId} data-disabled={disabled}>
        <div className={`${disabled ? styles.BaseControlDisabled : ""} ${isOpen ? styles.BaseControlOpen : styles.BaseControlClosed} ${borderClass()}`}
             tabIndex={0}>
          <div className={styles.SelectedValue} data-hj-suppress={personalData}>
            {titleComponent}
          </div>
          <div className={`${isOpen ? styles.IconOpen : styles.Icon} ${iconClassName ?? ""}`}>
            <MaterialIcon name="expand_more"/>
          </div>
        </div>

        <div className={`${isOpen ? styles.DropdownOpen : styles.DropdownClosed}`}>
          {items.map(item => {
            if (item.separator) {
              return <div key={item.id} className={styles.Separator}>
                <Separator/>
              </div>
            } else {
              const itemComponent = (typeof item.component === "string" || typeof item.component === "number")
                ? <div>{item.component}</div>
                : item.component
              return <div key={item.id} className={item.selected ? styles.ItemSelected : styles.Item} onClick={handleItemClick(item)} data-hj-suppress={personalData}>
                {itemComponent}
              </div>
            }
          })}
        </div>
      </div>
    </ClickOutsideHandler>
  )
}
